import { useEffect } from "react";

import { Formik, FormikErrors, useFormikContext } from "formik";
import { ScrollView, View } from "react-native";
import { Button, Text } from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import SummaryMap from "./SummaryMap";
import {
  FloatingButton,
  Form,
  KeyboardAwareView,
} from "../../../../components";
import FormikTextInput from "../../../ProductForm/components/FormikTextInput";
import useDeliveryFulfillments from "../../contexts/DeliveryFulfillmentsContext";
import {
  DeliveryFulfillmentsScreenProps,
  deliveryFulfillmentsRoutes,
} from "../../navigation/types";
import { LatLng, MapData, PickupAddress } from "../../types";

type Props = DeliveryFulfillmentsScreenProps<
  typeof deliveryFulfillmentsRoutes.pickupLocation
>;
export default function PickupLocationScreen({ navigation }: Props) {
  const { pickupAddress, savePickupAddress, selectedMapData } =
    useDeliveryFulfillments();

  const initialValues = getInitialValues(pickupAddress);

  async function onSubmit({ geolocation, ...rest }: FormValues) {
    if (!geolocation) return;
    await savePickupAddress({ ...rest, geolocation });
    navigation.goBack();
  }

  async function onValidate(values: FormValues) {
    const errors: FormikErrors<FormValues> = {};

    if (!values.address) {
      errors.address = "Los puntos de referencia son requeridos";
    }

    if (!values.contact_name) {
      errors.contact_name = "El nombre es requerido";
    }

    if (!values.phone) {
      errors.phone = "El número de WhatsApp es requerido";
    }

    return errors;
  }

  function navigateToMap() {
    navigation.navigate(deliveryFulfillmentsRoutes.map);
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={onValidate}
    >
      <PickupLocationForm
        selectedMapData={selectedMapData}
        navigateToMap={navigateToMap}
      />
    </Formik>
  );
}

type FormValues = {
  contact_name: string;
  address: string;
  phone: string;
  additional_instructions: string;
  reverse_geolocation_name: string | null;
  geolocation: LatLng | null;
};

function getInitialValues(address: PickupAddress | null): FormValues {
  if (!address)
    return {
      contact_name: "",
      address: "",
      phone: "",
      additional_instructions: "",
      reverse_geolocation_name: null,
      // geolocation: null,
      geolocation: {
        latitude: 8.9824,
        longitude: -79.5199,
      },
    };

  return address;
}

function useForm() {
  return useFormikContext<FormValues>();
}

type FormProps = {
  selectedMapData: MapData | null;
  navigateToMap: () => void;
};
function PickupLocationForm({ navigateToMap, selectedMapData }: FormProps) {
  const tailwind = useTailwind();

  const { values, errors, touched, handleSubmit, isSubmitting, setFieldValue } =
    useForm();

  function errorMessage(field: keyof FormValues) {
    return errors[field] && touched[field] ? errors[field] : undefined;
  }

  useEffect(() => {
    if (selectedMapData) {
      setFieldValue("geolocation", selectedMapData.position);
      setFieldValue("reverse_geolocation_name", selectedMapData.text);
    }
  }, [selectedMapData]);

  return (
    <KeyboardAwareView>
      <ScrollView bounces={false}>
        <View style={tailwind("m-4 gap-4 pb-24")}>
          <Text variant="titleMedium">Dirección</Text>

          <View style={tailwind("gap-1")}>
            <SummaryMap onPress={navigateToMap} position={values.geolocation} />

            <View style={tailwind("flex-row items-center")}>
              <View style={tailwind("flex-1")}>
                {values.reverse_geolocation_name && (
                  <Text>{values.reverse_geolocation_name}</Text>
                )}
              </View>
              <Button mode="text" onPress={navigateToMap}>
                Editar
              </Button>
            </View>
          </View>

          <Form.Field
            label="Puntos de referencia"
            required
            info="Utiliza puntos de referencia para facilitar la entrega"
            error={errorMessage("address")}
          >
            <FormikTextInput
              fieldName="address"
              placeholder="e.g.: Calle 50 frente al parque Harry Strunz. A un costado de la farmacia."
              multiline
              returnKeyType="default"
            />
          </Form.Field>

          <Form.Field
            label="Nombre"
            required
            error={errorMessage("contact_name")}
          >
            <FormikTextInput
              fieldName="contact_name"
              placeholder="¿Quién entrega la orden?"
            />
          </Form.Field>

          <Form.Field
            label="Whatsapp"
            icon="whatsapp"
            required
            error={errorMessage("phone")}
            info="Ingresa un número válido de WhatsApp."
          >
            <FormikTextInput
              fieldName="phone"
              placeholder="Número de WhatsApp"
            />
          </Form.Field>

          <Form.Field label="Instrucciones adicionales (opcional)">
            <FormikTextInput
              fieldName="additional_instructions"
              placeholder="e.g.: tocar timbre en puerta azul"
            />
          </Form.Field>
        </View>
      </ScrollView>
      <FloatingButton
        text="Guardar"
        mode="contained"
        onPress={handleSubmit}
        disabled={isSubmitting}
        loading={isSubmitting}
      />
    </KeyboardAwareView>
  );
}
